import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { ArrowRight, Check, Lock } from "lucide-react";
import { LandingHeader } from "@/components/landing-header";
import { useCalendlyPopup } from "@/hooks/use-calendly-popup";
import { attachCalendlyTracking } from "@/lib/analytics/calendly-tracking";
import { CALENDLY_30MIN_URL } from "@/lib/cta";
import { seo } from "@/lib/seo";

const STRIPE_CHECKOUT_URL = "https://buy.stripe.com/eVq00i7mO2jRaP3cUTeAg0c";

const LP_PATH = "/lp/intelligent-pr/pricing";
const LP_TITLE = "Intelligent PR — Pricing";
const LP_DESCRIPTION =
  "Activate the Intelligent PR Engine — 20 research-driven press releases drafted and programmatically syndicated every month for $1,499/mo.";

const FEATURES: { title: string; description: string }[] = [
  {
    title: "16 unique market report press releases",
    description:
      "Researched, drafted, generated, and programmatically syndicated hands-free every month to position you as a market leader.",
  },
  {
    title: "Syndication to 200+ News Sites",
    description: "145+ guaranteed placements with a weekly report.",
  },
  {
    title: "Persistent Brand Context Layer",
    description:
      "Securely maps, stores, and retains your unique positioning, product architecture, and target buyer personas so the engine never breaks brand rules.",
  },
  {
    title: "Fully Automated Asset Generation",
    description:
      "Includes custom-branded, search-optimized header graphics, meaning execution is entirely hands-free for you.",
  },
  {
    title: "Editorial Review Dashboard",
    description:
      "Absolute administrative control. 1-click approvals let you review, tweak, and deploy system drafts instantly.",
  },
  {
    title: "Weekly Footprint Audits",
    description:
      "Real-time traffic, headline impression, and live placement analytics delivered straight to your workspace terminal.",
  },
];

export const Route = createFileRoute("/lp/intelligent-pr/pricing")({
  head: () =>
    seo({
      title: LP_TITLE,
      description: LP_DESCRIPTION,
      path: LP_PATH,
      type: "website",
      noindex: true,
    }),
  component: PricingPage,
});

function PricingPage() {
  const openCalendlyPopup = useCalendlyPopup();
  useEffect(() => attachCalendlyTracking(), []);
  const openCalendly = () => openCalendlyPopup(CALENDLY_30MIN_URL);

  return (
    <>
      <LandingHeader ctaLabel="Chat with us" onClick={openCalendly} />

      {/* SECTION 1 — HERO */}
      <section className="bg-neutral-950 px-6 pt-20 pb-16">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white text-balance mb-6">
            Guaranteed Media Coverage. Powered by Intelligent PR.
          </h1>
          <p className="text-lg text-white/75 text-pretty">
            Your system configuration is initialized. Axy.digital is ready to map your in-depth
            market intelligence into data-rich research reports and programmatically syndicate them
            to build permanent backlinks, domain authority, and AI search visibility on autopilot.
          </p>
        </div>
      </section>

      {/* SECTIONS 2 & 3 — PRICING MATRIX + BILLING */}
      <section className="bg-neutral-950 px-6 pb-16">
        <div className="max-w-xl mx-auto rounded-xl border border-neutral-800 bg-neutral-900 p-8">
          <h2 className="text-2xl font-semibold tracking-tight text-white mb-4">
            Intelligent PR engine
          </h2>

          <p className="text-base text-white/80 leading-relaxed mb-8">{"\n"}</p>

          <ul className="space-y-5 mb-8">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex items-start gap-3">
                <Check className="size-4 mt-1 text-pink-soft shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white">{f.title}</p>
                  <p className="text-sm text-white/60 leading-relaxed">{f.description}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="border-t border-neutral-800 pt-8">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-lg text-white/50 line-through">$1,499</span>
              <span className="text-white/60 text-sm">/mo normally</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl font-semibold tracking-tight text-white">$1,124.25</span>
              <span className="text-white/60">/ month</span>
            </div>
            <p className="text-sm text-white/60 mb-8">
              25% off your first 3 months with a 3-month commitment.
            </p>

            <a
              href={STRIPE_CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full w-full px-6 py-3 text-sm md:text-base font-medium inline-flex items-center justify-center gap-2"
            >
              Deploy Intelligent PR Engine
              <ArrowRight className="size-4" />
            </a>

            <p className="mt-4 text-xs text-white/55 flex items-start gap-2 leading-relaxed">
              <Lock className="size-3.5 shrink-0 mt-0.5" />
              <span>
                Secure 256-bit encrypted checkout. We open onboarding slots weekly to protect
                network distribution health and domain allocation.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 — DE-RISK OFF-RAMP */}
      <section className="bg-neutral-950 px-6 pb-24">
        <div className="max-w-xl mx-auto border-t border-neutral-800 mt-12 pt-12 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-3">
            Chat with us before committing
          </h2>
          <p className="text-white/70 mb-8 text-pretty">
            A 6-month digital authority footprint is a serious strategic commitment. If you want to
            review your industry vertical parameters, inspect our live media nodes, or see a live
            demo of how the Intelligent PR engine works, let's look at your domain together.
          </p>
          <button
            type="button"
            onClick={openCalendly}
            className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-6 py-3 text-sm md:text-base font-medium inline-flex items-center gap-2"
          >
            Book a 30-minute strategy call with Robin, co-founder and CEO
            <ArrowRight className="size-4" />
          </button>
        </div>
      </section>
    </>
  );
}
